import { buildFieldPath, getNestedValue } from '@utils/nestedAccordion';
import {
  type CountryCode,
  getCountries,
  getCountryCallingCode,
  isValidPhoneNumber as isValidLibPhoneNumber,
  parsePhoneNumberFromString,
  validatePhoneNumberLength,
} from 'libphonenumber-js';

import type { FieldGroup, FormField } from '@/types/formSchema';

/**
 * Every country calling code libphonenumber knows, as bare digit strings
 * ("1", "44", "91"). Used to recognise a value that is a dial code and nothing else.
 */
const CALLING_CODES: ReadonlySet<string> = new Set(
  getCountries().map(country => getCountryCallingCode(country)),
);

/**
 * Region assumed when a number is entered without a country code. The
 * specialty-pharmacy phone/fax fields are free-text and almost always domestic
 * US numbers, so we interpret bare national numbers as US.
 */
const DEFAULT_PHONE_REGION: CountryCode = 'US';

/**
 * Plain `text`/`number` schema fields (no react-phone-input dial-code prefix)
 * that should still be validated as phone numbers. Keyed by field id; covers
 * both phone and fax numbers (a fax number obeys the same numbering rules).
 */
const PHONE_VALIDATED_FIELD_IDS = new Set<string>([
  'specialty_pharmacy_phone_number',
  'specialty_pharmacy_phone_number_2',
  'specialty_pharmacy_phone_number_3',
  'specialty_pharmacy_fax_number',
  'specialty_pharmacy_fax_number_2',
  'specialty_pharmacy_fax_number_3',
]);

interface PhoneValidationOptions {
  /**
   * True when the value carries a dialing country code without a leading `+`
   * (react-phone-input-2 storage, e.g. `15551234567`). When false, a bare
   * national number is interpreted against {@link DEFAULT_PHONE_REGION}.
   */
  hasDialCode?: boolean;
  /** Noun used in messages — "Phone" or "Fax". */
  noun?: string;
}

/**
 * Resolve the input string + default country to hand to libphonenumber-js.
 * An explicit `+` prefix always wins (country parsed from the number). For
 * dial-code values we restore the `+`; for bare national numbers we keep the
 * value as-is and supply a default region.
 */
function resolveParseArgs(
  trimmed: string,
  hasDialCode: boolean,
): { input: string; defaultCountry?: CountryCode } {
  if (trimmed.startsWith('+')) {
    return { input: `+${trimmed.slice(1).replace(/\D/g, '')}` };
  }
  if (hasDialCode) {
    return { input: `+${trimmed.replace(/\D/g, '')}` };
  }
  return { input: trimmed, defaultCountry: DEFAULT_PHONE_REGION };
}

/**
 * True when the value is a country dial code and nothing else — an empty field, not a
 * malformed number (LA-7358).
 *
 * `react-phone-input-2` is mounted with `country="us"`, so a phone field nobody has
 * typed into holds `"1"` — rendered as `+1` — rather than `""`. Parsing that yields
 * TOO_SHORT, which reported every untouched phone field on the case as invalid: the
 * message appeared on fields the rep never opened, could not be cleared by correcting
 * the field they were actually editing, and blocked Save and Confirm outright.
 *
 * Emptiness is the required-field rules' business, not format validation's — the same
 * reason an empty string returns null.
 *
 * Scoped to values that actually carry a dial code. For the free-text
 * {@link PHONE_VALIDATED_FIELD_IDS} a lone "1" is a digit the rep typed, so it stays
 * invalid.
 */
function isDialCodeOnly(trimmed: string, hasDialCode: boolean): boolean {
  if (!hasDialCode && !trimmed.startsWith('+')) return false;
  const digits = trimmed.replace(/\D/g, '');
  return digits === '' || CALLING_CODES.has(digits);
}

// libphonenumber-js length-check codes that mean "wrong number of digits".
const TOO_SHORT_CODES = new Set(['TOO_SHORT', 'INVALID_LENGTH']);

function lengthErrorMessage(code: string, noun: string): string {
  if (TOO_SHORT_CODES.has(code)) return `${noun} number is too short`;
  if (code === 'TOO_LONG') return `${noun} number is too long`;
  if (code === 'INVALID_COUNTRY') return 'Unrecognized country code';
  return `Enter a valid ${noun.toLowerCase()} number`;
}

/**
 * Validate a phone/fax number against its country code's expected length/format
 * using Google's libphonenumber metadata. Length rules are country-specific:
 * e.g. `+1` (US/NANP) expects 10 national digits, `+86` (China mobile) 11.
 *
 * Returns a human-readable error message when invalid, or `null` when the
 * number is valid. Empty input returns `null` — emptiness is governed by the
 * separate "required field" rules, not by format validation.
 */
export function getPhoneValidationError(
  rawValue: string | null | undefined,
  options: PhoneValidationOptions = {},
): string | null {
  if (rawValue === null || rawValue === undefined) return null;
  const trimmed = String(rawValue).trim();
  if (trimmed === '') return null;

  const { hasDialCode = true, noun = 'Phone' } = options;

  // An untouched phone control counts as EMPTY, not malformed — see isDialCodeOnly.
  if (isDialCodeOnly(trimmed, hasDialCode)) return null;

  const { input, defaultCountry } = resolveParseArgs(trimmed, hasDialCode);

  // Length check first — gives the most specific too-short / too-long message,
  // which is what callers most often need ("wrong number of digits").
  const lengthError = validatePhoneNumberLength(input, defaultCountry);
  if (lengthError) {
    return lengthErrorMessage(lengthError, noun);
  }

  // Length is plausible but the number may still be invalid (e.g. an area code
  // that doesn't exist). Fall back to a full validity check.
  if (!isValidLibPhoneNumber(input, defaultCountry)) {
    return `Enter a valid ${noun.toLowerCase()} number`;
  }

  return null;
}

/**
 * Decide whether a field should be validated as a phone/fax number and, if so,
 * how. Returns the validation options, or `null` when the field isn't a phone
 * field at all.
 *
 * Two kinds of fields qualify:
 *   - `phone`-typed fields (react-phone-input-2; dial code embedded), and
 *   - free-text fields whose id is in {@link PHONE_VALIDATED_FIELD_IDS}
 *     (interpreted against the default region).
 */
function phoneFieldConfig(field: {
  id: string;
  type?: string;
}): Required<PhoneValidationOptions> | null {
  const isPhoneType = field.type === 'phone';
  const isPhoneId = PHONE_VALIDATED_FIELD_IDS.has(field.id);
  if (!isPhoneType && !isPhoneId) return null;
  return { hasDialCode: isPhoneType, noun: field.id.includes('fax') ? 'Fax' : 'Phone' };
}

/**
 * Resolve a field definition from its field path. Array indices are skipped when
 * descending the schema (the schema describes the shape of every array item, not
 * per-index), so `drug.0.fax_number` resolves through `drug` → `fax_number`.
 *
 * BOTH index notations are accepted, because the two families of output panes key
 * their rows differently and both feed this function (LA-7358):
 *   - the accordion boards use dot indices — `drug.0.fax_number`
 *   - the Human Call board and Call Script use bracket indices — `drug[0].fax_number`
 *     (see `utils/humanCall/fieldTree.ts`, which builds every leaf key that way)
 *
 * Handling only the dot form is what made the Call Script silently skip validation
 * for every array-nested phone/fax field: the segment `drug[0]` matched no schema
 * field id, so this returned null and `getPhoneFieldError` reported "not a phone
 * field" rather than an invalid number.
 *
 * Returns `null` when no field matches.
 */
function findFieldByPath(
  groups: Pick<FieldGroup, 'fields'>[] | undefined,
  path: string,
): FormField | null {
  if (!groups) return null;
  const segments = path
    .split('.')
    // Strip bracket indices (`drug[0]` → `drug`) before dropping bare numeric
    // segments, so both notations reduce to the same schema walk.
    .map(seg => seg.replace(/\[\d+\]/g, ''))
    .filter(seg => seg !== '' && !/^\d+$/.test(seg));
  if (segments.length === 0) return null;

  let pool: FormField[] | undefined = groups.flatMap(group => group.fields);
  let current: FormField | undefined;
  for (const seg of segments) {
    current = pool?.find(field => field.id === seg);
    if (!current) return null;
    pool = current.fields;
  }
  return current ?? null;
}

/**
 * Validate a single field's value by its (possibly nested) dot-path. Returns an
 * error message when the field is a phone/fax field and its value is invalid,
 * or `null` otherwise (not a phone field, or valid). Used for live, on-change
 * validation. `value` is the field's current value — callers reading nested
 * data should resolve it with `getNestedValue(data, fieldPath)`.
 */
/**
 * What {@link coercePhoneFieldValue} decided about a value being written programmatically.
 *
 * `unchanged` — not a phone field, or already fine as stored. Write it as-is.
 * `normalized` — a real number in the wrong encoding. Write `value` instead.
 * `unusable` — not a phone number at all. Do not write; show `message`.
 */
export type PhoneCoercion =
  | { status: 'unchanged' }
  | { status: 'normalized'; value: string }
  | { status: 'unusable'; message: string };

/**
 * Re-encode a value for `react-phone-input-2`'s storage form, or report it unusable.
 * Split out of {@link coercePhoneFieldValue} to keep that function within the
 * complexity budget; `existing` is the validation message the value already carries.
 */
function encodeForPhoneControl(trimmed: string, existing: string | null): PhoneCoercion {
  // Empty / dial-code-only: an untouched control, nothing to encode.
  if (isDialCodeOnly(trimmed, true)) return { status: 'unchanged' };

  // Read it as a NATIONAL number FIRST. The order is the whole point: an extracted value
  // did not come from the control, so it is far likelier to be a bare national number
  // than an encoded one — and a bare US number frequently parses as a perfectly valid
  // number under some OTHER country code ("2015550123" reads as +20…). So "it already
  // validates as stored" cannot be used to decide the encoding is right.
  const asNational = parsePhoneNumberFromString(trimmed, DEFAULT_PHONE_REGION);
  if (asNational?.isValid()) {
    const encoded = `${asNational.countryCallingCode}${asNational.nationalNumber}`;
    // A value already in storage form round-trips to itself, so report it untouched:
    // `normalized` is reserved for a value that actually changed.
    return encoded === trimmed ? { status: 'unchanged' } : { status: 'normalized', value: encoded };
  }

  // Not a national number — it may already be an encoded international one (a UK number
  // stored as "442071234567"). Leave those exactly as they are.
  if (!existing) return { status: 'unchanged' };
  return { status: 'unusable', message: existing };
}

/**
 * Decide how to write a value into a phone field that did NOT come from the phone
 * control itself — today, the script's "Accept <extracted value>" button (LA-7358).
 *
 * `react-phone-input-2` stores a number as its country code followed by the national
 * digits, with no `+` ("12342341241"), and renders whatever it is given by reading the
 * leading digits as a country code. It never rewrites the stored value — it fires no
 * `onChange` when its `value` prop changes — so a raw write lands verbatim and is
 * displayed under that rule. Handing it a bare national number like "2342341241" makes
 * it show `+234 234 124 1`: a Nigerian number, and flagged too short. Typing is
 * unaffected, since there the control produces the encoding itself.
 *
 * Conservative on purpose. A value that already validates is never touched, so a real
 * international number stored without a `+` (a valid UK number, say) cannot be re-read
 * as US and rewritten. Only a value that would otherwise be reported invalid is parsed
 * against {@link DEFAULT_PHONE_REGION}, and only a confident parse is rewritten.
 *
 * Anything that cannot be rescued comes back `unusable` rather than being written and
 * then flagged: filling the box with a value the rep cannot save also hides the Accept
 * chip (`shouldShowExtractedValue` compares against the field's own value), leaving no
 * way to retry and nothing to fall back to.
 */
export function coercePhoneFieldValue(
  field: Pick<FormField, 'id' | 'type'> | undefined,
  value: unknown,
): PhoneCoercion {
  const config = field ? phoneFieldConfig(field) : null;
  // Not a phone/fax field — nothing to encode.
  if (!config) return { status: 'unchanged' };

  const existing = getPhoneValidationError(value as string | null | undefined, config);

  // Only `phone`-typed fields carry the dial-code encoding. The free-text
  // PHONE_VALIDATED_FIELD_IDS hold exactly what the rep typed, so there is nothing to
  // re-encode: such a value is either fine or simply invalid.
  if (!config.hasDialCode) {
    return existing ? { status: 'unusable', message: existing } : { status: 'unchanged' };
  }
  return encodeForPhoneControl(String(value ?? '').trim(), existing);
}

export function getPhoneFieldError(
  groups: Pick<FieldGroup, 'fields'>[] | undefined,
  fieldPath: string,
  value: unknown,
): string | null {
  const field = findFieldByPath(groups, fieldPath);
  if (!field) return null;
  const config = phoneFieldConfig(field);
  if (!config) return null;
  return getPhoneValidationError(value as string | null | undefined, config);
}

/**
 * How array item indices are spelled in the returned keys.
 *
 * `dot` (the default) matches the accordion boards' `data-field-id`
 * (`drug.0.fax_number`); `bracket` matches the Human Call board and Call Script,
 * whose row keys come from `utils/humanCall/fieldTree.ts` (`drug[0].fax_number`).
 *
 * The caller has to say which, because these keys are looked up directly against
 * a pane's row keys (`fieldErrors[field.key]`) and queried as
 * `[data-field-id="…"]` when jumping to the first failure. Returning the wrong
 * notation is invisible — the map is non-empty, so the save is blocked and the
 * header says "Validation errors found.", but no row renders a message and the
 * jump finds nothing (LA-7358).
 */
// Not exported: callers pass the literal `'bracket'` / `'dot'`, so an exported alias
// would have no consumer and the knip gate hard-fails on unused exported types.
type FieldPathStyle = 'dot' | 'bracket';

/**
 * Recursively walk field groups — descending into `object` fields and every
 * item of `array` fields — and validate every phone/fax field present in
 * `data`. Returns a map of `fieldPath -> error message` for the ones that fail,
 * keyed the way {@link FieldPathStyle} describes. Empty when everything is
 * valid. Used to gate the output save action. Insertion order follows schema
 * document order, so the first key is the first failing field.
 */
export function collectPhoneFieldErrors(
  groups: Pick<FieldGroup, 'fields'>[] | undefined,
  data: Record<string, any> | null | undefined,
  pathStyle: FieldPathStyle = 'dot',
): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!groups || !data) return errors;

  // Two paths are tracked side by side, because they are not interchangeable:
  //   - `dataPath` always uses dot indices, since `getNestedValue` splits on "."
  //     and would look up the literal key "drug[0]" given a bracket path.
  //   - `keyPath` uses the caller's notation and is what ends up in `errors`.
  // They differ only inside arrays, so for `pathStyle: 'dot'` they stay identical.
  const walk = (fields: FormField[], dataBase: string, keyBase: string) => {
    for (const field of fields) {
      const dataPath = buildFieldPath(dataBase, field.id);
      const keyPath = buildFieldPath(keyBase, field.id);

      // Descend into nested object/array fields so phone fields at any depth
      // are validated. Arrays validate each existing item by index.
      if (field.fields && field.fields.length > 0) {
        const value = getNestedValue(data, dataPath);
        if (Array.isArray(value)) {
          value.forEach((_, index) =>
            walk(
              field.fields!,
              `${dataPath}.${index}`,
              pathStyle === 'bracket' ? `${keyPath}[${index}]` : `${keyPath}.${index}`,
            ),
          );
        } else {
          walk(field.fields, dataPath, keyPath);
        }
        continue;
      }

      const config = phoneFieldConfig(field);
      if (!config) continue;
      const error = getPhoneValidationError(getNestedValue(data, dataPath), config);
      if (error) errors[keyPath] = error;
    }
  };

  for (const group of groups) walk(group.fields, '', '');
  return errors;
}

/**
 * Restore the `+` that `react-phone-input-2` drops from a `phone`-typed value
 * ("380952173481" → "+380952173481"). The digits are read exactly the way
 * {@link resolveParseArgs} reads them for validation — as a dial-code-encoded number —
 * so the stored value means the same number it was validated as.
 *
 * Left untouched: non-strings, empty values, values that already start with `+`, and a
 * dial code on its own (an untouched control holds "1"; writing "+1" would turn an
 * empty field into a malformed one — see {@link isDialCodeOnly}).
 */
function withPlusPrefix(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (trimmed === '' || trimmed.startsWith('+') || isDialCodeOnly(trimmed, true)) return value;
  const digits = trimmed.replace(/\D/g, '');
  return digits ? `+${digits}` : value;
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Return `data` with every `phone`-typed field, at any depth, carrying a leading `+`
 * (BV-2328). Applied to case input data before it is written: the AI caller dials the
 * stored value verbatim, and without the `+` the carrier cannot tell the country code
 * from the number.
 *
 * Only `phone`-typed fields change. The free-text {@link PHONE_VALIDATED_FIELD_IDS}
 * hold a national number exactly as typed, with no dial code to prefix.
 *
 * Copy-on-write: unchanged branches keep their identity and `data` is never mutated, so
 * non-JSON values (pending `File` uploads) pass through untouched. Keys absent from
 * `data` stay absent, which keeps a partial (diffed) payload partial.
 */
export function normalizePhoneFieldsForSave<T extends Record<string, unknown>>(
  groups: Pick<FieldGroup, 'fields'>[] | undefined,
  data: T,
): T {
  if (!groups || !isPlainObject(data)) return data;

  // A nested field's value: an array normalizes each object item, an object recurses.
  const walkNested = (children: FormField[], value: unknown): unknown => {
    if (Array.isArray(value)) {
      const items = value.map(item => (isPlainObject(item) ? walk(children, item) : item));
      return items.some((item, i) => item !== value[i]) ? items : value;
    }
    return isPlainObject(value) ? walk(children, value) : value;
  };

  const walk = (fields: FormField[], node: Record<string, unknown>): Record<string, unknown> => {
    let out = node;
    for (const field of fields) {
      if (!(field.id in node)) continue;
      const value = node[field.id];
      let next = value;
      if (field.fields && field.fields.length > 0) {
        next = walkNested(field.fields, value);
      } else if (field.type === 'phone') {
        next = withPlusPrefix(value);
      }
      if (next !== value) {
        if (out === node) out = { ...node };
        out[field.id] = next;
      }
    }
    return out;
  };

  let result: Record<string, unknown> = data;
  for (const group of groups) result = walk(group.fields, result);
  return result as T;
}

/**
 * Validates if a phone number is in a valid format.
 * Supports:
 *   - E.164 format: `+<country_code><number>` (7–15 digits after `+`)
 *   - US numbers without country code: 10 digits starting with 2–9
 *   - International numbers without `+` prefix: 7–15 digits (e.g., `919005889347`)
 */
export function isValidPhoneNumber(phoneNumber: string): boolean {
  if (!phoneNumber || typeof phoneNumber !== 'string') {
    return false;
  }

  const trimmed = phoneNumber.trim();

  // E.164 format: + followed by 7-15 digits
  if (trimmed.startsWith('+')) {
    const digits = trimmed.substring(1).replace(/\D/g, '');
    return digits.length >= 7 && digits.length <= 15;
  }

  const cleaned = trimmed.replace(/\D/g, '');

  // US number without country code: 10 digits starting with 2-9 (NANP format)
  if (cleaned.length === 10) {
    return /^[2-9]\d{9}$/.test(cleaned);
  }

  // International number without `+`: 7-15 digits (matches E.164 length range)
  return cleaned.length >= 7 && cleaned.length <= 15;
}

/**
 * Formats a phone number into E.164 format.
 *   - `+xxx`         → `+xxx` (preserved)
 *   - 10-digit US    → `+1xxxxxxxxxx`
 *   - International  → `+xxx` (prepends `+`)
 */
export function formatPhoneNumber(phoneNumber: string): string {
  if (!isValidPhoneNumber(phoneNumber)) {
    throw new Error('Invalid phone number format');
  }

  const trimmed = phoneNumber.trim();

  // Already in E.164 format
  if (trimmed.startsWith('+')) {
    const digits = trimmed.substring(1).replace(/\D/g, '');
    return `+${digits}`;
  }

  const cleaned = trimmed.replace(/\D/g, '');

  // US number without country code: add +1 prefix
  if (cleaned.length === 10) {
    return `+1${cleaned}`;
  }

  // International number without `+`: prepend `+`
  return `+${cleaned}`;
}

/**
 * Helper function to validate and format phone number with logging
 */
function validateAndFormatPhone(phoneNumber: string, fieldId: string): string | null {
  if (isValidPhoneNumber(phoneNumber)) {
    try {
      return formatPhoneNumber(phoneNumber);
    } catch {
      console.warn(`Invalid phone number format for field ${fieldId}:`);
    }
  }
  return null;
}

/**
 * Search for phone number in UI schema fields
 */
function searchPhoneInFields(inputGroups: FieldGroup[], inputData: any): string | null {
  for (const group of inputGroups) {
    for (const field of group.fields) {
      if (field.type === 'phone' && inputData[field.id]) {
        const phoneNumber = inputData[field.id].toString();
        const formatted = validateAndFormatPhone(phoneNumber, field.id);
        if (formatted) return formatted;
      }
    }
  }
  return null;
}

/**
 * Search for phone number in common field names
 */
function searchPhoneInCommonFields(inputData: any): string | null {
  const commonPhoneFields = [
    'primary_phone',
    'insurance_phone',
    'phone_number',
    'phone',
    'mobile_number',
    'mobile',
    'contact_number',
  ];
  for (const fieldName of commonPhoneFields) {
    if (inputData[fieldName]) {
      const phoneNumber = inputData[fieldName].toString();
      const formatted = validateAndFormatPhone(phoneNumber, fieldName);
      if (formatted) return formatted;
    }
  }
  return null;
}

/**
 * Extract phone number from case data based on UI schema
 * Looks for fields with type 'phone' in the UI schema
 */
export function getPhoneFromUISchema(inputGroups: FieldGroup[], inputData: any): string | null {
  if (!inputGroups || !inputData) {
    return null;
  }

  // Search through UI schema fields first
  const phoneFromFields = searchPhoneInFields(inputGroups, inputData);
  if (phoneFromFields) return phoneFromFields;

  // Fallback to common phone field names
  return searchPhoneInCommonFields(inputData);
}

/**
 * Get a random index for phone number selection
 * Uses timestamp to ensure different number each time
 */
export function getRandomPhoneIndex(): number {
  return Math.floor(Math.random() * 100);
}
